import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./AuthorListingsRow.scss";
import { thumbImgProps } from "../../utils/imageUrl";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const posterThemes = [
  "blue",
  "violet",
  "graphite",
  "green",
  "bronze",
  "wine",
  "ocean",
  "sand",
];

const getStableTheme = (item, title) => {
  const source = String(item?.listingId || item?.id || title || "author");
  let hash = 0;

  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }

  return posterThemes[Math.abs(hash) % posterThemes.length];
};

const getTitleSizeClass = (title) => {
  const len = String(title || "").trim().length;

  if (len <= 18) return "is-short";
  if (len <= 42) return "is-medium";
  return "is-long";
};

const isWantedListing = (item) => {
  const raw =
    item?.listingType ||
    item?.typeRaw ||
    item?.type ||
    item?.dealType ||
    item?.transferType ||
    "";

  return String(raw).trim().toLowerCase() === "wanted";
};

const AuthorListingCard = ({ item }) => {
  const lid = item.listingId || item.id;
  const title = item.title || "Без назви";

  const thumb =
    (Array.isArray(item.photos) && item.photos[0]) ||
    item.mainPhoto ||
    item.photo ||
    null;

  const priceNum = Number(item.price || 0);
  const wanted = isWantedListing(item);
  const theme = getStableTheme(item, title);
  const titleSizeClass = getTitleSizeClass(title);

  return (
    <Link to={`/listing/${lid}`} className="author-listing-card">
      <div className="author-listing-card__media">
        {thumb ? (
          <img {...thumbImgProps(thumb, "(max-width: 600px) 45vw, 220px")} alt={title || "Фото"} />
        ) : (
          <div className={`author-listing-card__poster author-listing-card__poster--${theme}`}>
            <div className="author-listing-card__poster-glow" />
            <div className={`author-listing-card__poster-title ${titleSizeClass}`}>
              {title}
            </div>
          </div>
        )}
      </div>

      <div className="author-listing-card__body">
        <div className="author-listing-card__title">
          {title}
        </div>

        {wanted ? (
          <div className="author-listing-card__price author-listing-card__price--wanted">
            Шукаю
          </div>
        ) : (
          Number.isFinite(priceNum) &&
          priceNum > 0 && (
            <div className="author-listing-card__price">
              {priceNum.toLocaleString("uk-UA")} {item.currency || "₴"}
            </div>
          )
        )}
      </div>
    </Link>
  );
};

const AuthorListingsRow = ({ listingId, authorId, authorUsername }) => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tried, setTried] = useState(false);

  const safeAuthorId = useMemo(() => String(authorId || "").trim(), [authorId]);
  const safeListingId = useMemo(() => String(listingId || "").trim(), [listingId]);

  useEffect(() => {
    if (!safeAuthorId || !safeListingId) return;

    let alive = true;

    const fetchAuthorListings = async () => {
      setLoading(true);
      setTried(false);

      try {
        // Scoped to this author via the existing userId-indexed endpoint
        // instead of fetching every listing on the marketplace and filtering
        // client-side (P0 #1, 2026-08-29 RAM/stability fix) — that endpoint
        // already returns only this author's active listings, sorted newest
        // first, so only the "not the current listing" filter is still
        // needed here.
        const { data } = await axios.get(
          `${API_BASE}/api/listings/user/${encodeURIComponent(safeAuthorId)}`,
          { validateStatus: (s) => s >= 200 && s < 300 }
        );

        if (!alive) return;

        const all = Array.isArray(data) ? data : [];

        const filtered = all
          .filter((it) => {
            const id = String(it.listingId || it.id || "");
            return id && id !== safeListingId;
          })
          .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0))
          .slice(0, 6);

        setItems(filtered);
      } catch {
        if (alive) setItems([]);
      } finally {
        if (alive) {
          setLoading(false);
          setTried(true);
        }
      }
    };

    fetchAuthorListings();

    return () => {
      alive = false;
    };
  }, [safeAuthorId, safeListingId]);

  if (!loading && tried && items.length === 0) return null;

  return (
    <section className="author-listings-row">
      <div className="author-listings-row__head">
        <h3 className="author-listings-row__title">
          Інші оголошення автора
        </h3>

        <button
          type="button"
          className="author-listings-row__more"
          onClick={() => navigate(`/profile/${safeAuthorId}?tab=listings`)}
        >
          Дивитися всі
        </button>
      </div>

      {loading && !items.length && (
        <div className="author-listings-row__items author-listings-row__items--skeletons">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="author-listing-skel">
              <div className="author-listing-skel__media" />
              <div className="author-listing-skel__line" />
              <div className="author-listing-skel__line author-listing-skel__line--short" />
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="author-listings-row__items">
          {items.map((item) => (
            <AuthorListingCard key={item.listingId || item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
};

export default AuthorListingsRow;